import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import CurrencyInput from '../../components/input/CurrencyInput';
import DropDown from '../../components/dropdown/DropDown';
import DatePicker from '../../components/datepicker/DatePicker';
import { FormValues } from '../../pages/MonetizationNew';
import { useEffect } from 'react';

interface CheckPromissoryExecutionSectionProps {
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  clearing?: number;
  isCheck: boolean;
  isCommercialEndorsement?: boolean;
}

const CheckPromissoryExecutionSection: React.FC<CheckPromissoryExecutionSectionProps> = ({
  formValues,
  updateFormValues,
  clearing,
  isCheck,
  isCommercialEndorsement = false,
}) => {
  const ivaOptions = [
    { id: true, label: 'SI' },
    { id: false, label: 'NO' },
  ];

  const clearingOptions = [
    { id: 0, label: 'NO' },
    { id: 1, label: '24 hs' },
    { id: 2, label: '48 hs' },
  ];

  // Note: liquidation date calculation is now handled in useMonetizationForm hook

  // Set clearing from API if it's a check
  useEffect(() => {
    if (isCheck && clearing !== undefined && clearing !== null) {
      updateFormValues({ clearing });
    }
  }, [clearing, isCheck]);

  return (
    <Card margin={{ top: '4' }}>
      <div style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}>
        <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
          Ejecución de aval
        </H>
      </div>

      {/* Fila 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
        <Card>
          <P withMargin={false} variant='neutral' type='4' modifier='strong'>
            Fecha de monetización *
          </P>
          <Card margin={{ top: '2' }}>
            <DatePicker
              defaultValue={formValues.monetization_date}
              onChange={(date: string) => updateFormValues({ monetization_date: date })}
              disabled={false}
              displayFormat="DD/MM/AA"
              minDate={formValues.issue_date ? new Date(formValues.issue_date) : undefined}
            />
          </Card>
        </Card>
        {!isCommercialEndorsement && (
          <Input
            label="Código CVBA *"
            id='execution-cvba-code'
            type='text'
            size='md'
            value={formValues.monetization_cvba_code || ''}
            onChange={(e) => updateFormValues({ monetization_cvba_code: e.target.value })}
            inputStatus="normal"
          />
        )}
        <CurrencyInput
          label="Monto Total en Pesos *"
          id='execution-total-amount'
          size='md'
          value={formValues.monetization_amount}
          onValueChange={(val) => updateFormValues({ monetization_amount: val })}
          inputStatus="normal"
        />
        <DropDown
          id='execution-iva-dropdown'
          label="IVA *"
          items={ivaOptions}
          size='md'
          title={formValues.iva !== null && formValues.iva !== undefined ? (formValues.iva ? 'SI' : 'NO') : 'Seleccionar'}
          onChange={(value) => {
            const selected = ivaOptions.find(opt => opt.label === value);
            updateFormValues({ iva: selected?.id });
          }}
          marginTop="4px"
        />
        {isCommercialEndorsement && (
          <CurrencyInput
            label="Tasa de Descuento *"
            id='execution-discount-rate'
            size='md'
            value={formValues.monetization_discount_rate || null}
            onValueChange={(val) => updateFormValues({ monetization_discount_rate: val })}
            inputStatus="normal"
          />
        )}
      </div>

      {/* Fila 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
        {!isCommercialEndorsement && (
          <CurrencyInput
            label="Tasa de Descuento *"
            id='execution-discount-rate'
            size='md'
            value={formValues.monetization_discount_rate || null}
            onValueChange={(val) => updateFormValues({ monetization_discount_rate: val })}
            inputStatus="normal"
          />
        )}
        <CurrencyInput
          label="Intereses *"
          id='execution-interest'
          size='md'
          value={formValues.monetization_interest || null}
          onValueChange={(val) => updateFormValues({ monetization_interest: val })}
          inputStatus="normal"
        />
        <Input
          label="Fecha de liquidación"
          id='execution-liquidation-date'
          type='text'
          size='md'
          value={formValues.settlement_date || ''}
          readOnly
          modifier="weak"
        />
        {isCheck ? (
          <Input
            label="Plazo de liquidación *"
            id='execution-clearing-input'
            type='text'
            size='md'
            value={
              formValues.clearing !== null && formValues.clearing !== undefined
                ? clearingOptions.find(opt => opt.id === formValues.clearing)?.label || ''
                : ''
            }
            readOnly
            modifier="weak"
          />
        ) : (
          <DropDown
            id='execution-clearing-dropdown'
            label="Plazo de liquidación *"
            items={clearingOptions}
            size='md'
            title={
              formValues.clearing !== null && formValues.clearing !== undefined
                ? clearingOptions.find(opt => opt.id === formValues.clearing)?.label || 'Seleccionar'
                : 'Seleccionar'
            }
            onChange={(value) => {
              const selected = clearingOptions.find(opt => opt.label === value);
              updateFormValues({ clearing: selected?.id });
            }}
            marginTop="4px"
          />
        )}
        {isCommercialEndorsement && (
          <Input
            label="Alyc Compradora *"
            id='execution-buyer-alyc'
            type='text'
            size='md'
            value={formValues.buyer_alyc || ''}
            onChange={(e) => updateFormValues({ buyer_alyc: e.target.value })}
            inputStatus="normal"
          />
        )}
      </div>

      {/* Fila 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
        {!isCommercialEndorsement && (
          <Input
            label="Alyc Compradora *"
            id='execution-buyer-alyc'
            type='text'
            size='md'
            value={formValues.buyer_alyc || ''}
            onChange={(e) => updateFormValues({ buyer_alyc: e.target.value })}
            inputStatus="normal"
          />
        )}
        <Input
          label="Alyc Vendedora *"
          id='execution-seller-alyc'
          type='text'
          size='md'
          value={formValues.seller_alyc || ''}
          onChange={(e) => updateFormValues({ seller_alyc: e.target.value })}
          inputStatus="normal"
        />
      </div>
    </Card>
  );
};

export default CheckPromissoryExecutionSection;
